/**
 * Checkout Validators
 *
 * Step-by-step validation for checkout form
 *
 * @package Enterprise\BookingSystem
 */

import type {
	CheckoutFormData,
	ValidationResult,
	CustomerInformation,
	BillingAddress,
	ShippingAddress,
} from "./types";

/**
 * Validate email format
 */
const isValidEmail = (email: string): boolean => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(email);
};

/**
 * Validate phone format (basic validation)
 */
const isValidPhone = (phone: string): boolean => {
	// Remove all non-digit characters
	const digitsOnly = phone.replace(/\D/g, "");
	// Check if it has at least 10 digits
	return digitsOnly.length >= 10;
};

/**
 * Validate Step 1: Customer & Billing Information
 */
export const validateStep1 = (
	customer: CustomerInformation,
): ValidationResult => {
	const errors = validateBillingAddress(customer.billing);

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
	};
};

/**
 * Validate billing address
 */
const validateBillingAddress = (
	billing: BillingAddress,
): Record<string, string> => {
	const errors: Record<string, string> = {};

	if (!billing.first_name || billing.first_name.trim().length < 2) {
		errors.billing_first_name =
			"Billing first name must be at least 2 characters";
	}

	if (!billing.last_name || billing.last_name.trim().length < 2) {
		errors.billing_last_name =
			"Billing last name must be at least 2 characters";
	}

	if (!billing.email) {
		errors.billing_email = "Billing email is required";
	} else if (!isValidEmail(billing.email)) {
		errors.billing_email = "Please enter a valid billing email";
	}

	if (!billing.phone) {
		errors.billing_phone = "Billing phone is required";
	} else if (!isValidPhone(billing.phone)) {
		errors.billing_phone = "Please enter a valid billing phone number";
	}

	if (!billing.address_1 || billing.address_1.trim().length < 5) {
		errors.billing_address_1 = "Billing address is required";
	}

	if (!billing.city || billing.city.trim().length < 2) {
		errors.billing_city = "Billing city is required";
	}

	if (!billing.state || billing.state.trim().length < 2) {
		errors.billing_state = "Billing state is required";
	}

	if (!billing.postcode || billing.postcode.trim().length < 3) {
		errors.billing_postcode = "Billing postal code is required";
	}

	if (!billing.country || billing.country.trim().length !== 2) {
		errors.billing_country = "Billing country is required";
	}

	return errors;
};

/**
 * Validate shipping address
 */
const validateShippingAddress = (
	shipping: ShippingAddress,
): Record<string, string> => {
	const errors: Record<string, string> = {};

	if (!shipping.first_name || shipping.first_name.trim().length < 2) {
		errors.shipping_first_name =
			"Delivery first name must be at least 2 characters";
	}

	if (!shipping.last_name || shipping.last_name.trim().length < 2) {
		errors.shipping_last_name =
			"Delivery last name must be at least 2 characters";
	}

	if (!shipping.address_1 || shipping.address_1.trim().length < 5) {
		errors.shipping_address_1 = "Delivery address is required";
	}

	if (!shipping.city || shipping.city.trim().length < 2) {
		errors.shipping_city = "Delivery city is required";
	}

	if (!shipping.state || shipping.state.trim().length < 2) {
		errors.shipping_state = "Delivery state is required";
	}

	if (!shipping.postcode || shipping.postcode.trim().length < 3) {
		errors.shipping_postcode = "Delivery postal code is required";
	}

	if (!shipping.country || shipping.country.trim().length !== 2) {
		errors.shipping_country = "Delivery country is required";
	}

	if (!shipping.phone) {
		errors.shipping_phone = "Delivery phone is required";
	} else if (!isValidPhone(shipping.phone)) {
		errors.shipping_phone = "Please enter a valid delivery phone number";
	}

	return errors;
};

/**
 * Validate Step 2: Delivery Location
 */
export const validateStep2 = (
	delivery: CheckoutFormData["delivery"],
): ValidationResult => {
	let errors: Record<string, string> = {};

	// Validate shipping/delivery address (only if different from billing)
	if (!delivery.ship_to_billing) {
		errors = { ...errors, ...validateShippingAddress(delivery.shipping) };
	}

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
	};
};

/**
 * Validate Step 3: Payment Information
 */
export const validateStep3 = (
	payment: CheckoutFormData["payment"],
): ValidationResult => {
	const errors: Record<string, string> = {};

	// Payment method
	if (!payment.payment_method) {
		errors.payment_method = "Please select a payment method";
	}

	// Note: Actual card validation is handled by payment processor (Stripe, etc.)
	// We don't validate card details here for PCI compliance

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
	};
};

/**
 * Validate entire form (all steps)
 */
export const validateAllSteps = (
	formData: CheckoutFormData,
): ValidationResult => {
	const step1 = validateStep1(formData.customer);
	const step2 = validateStep2(formData.delivery);
	const step3 = validateStep3(formData.payment);

	return {
		isValid: step1.isValid && step2.isValid && step3.isValid,
		errors: {
			...step1.errors,
			...step2.errors,
			...step3.errors,
		},
	};
};
